/**
 * agentAnalytics.js — Unified agent usage helpers.
 *
 * Merges Ask-mode and Workflow-mode token data into a single card per
 * agent type so the UI shows one "Generalist Agent" card regardless of
 * which mode generated the usage.
 *
 * Ask shape:   analytics.generalist_usage
 *                → { aeri: {model, provider, input_tokens, output_tokens}, … }
 *
 * Workflow shape: workflowAnalytics.agent_usage.generalist.agents
 *                → same per-sub-agent shape
 *
 * The returned card array can be rendered identically in the user
 * dashboard (UsageDashboard) and the admin panel (AdminTable).
 */

/** Ordered list of agent types to check and display names. */
export const AGENT_TYPE_ORDER = [
  { key: 'generalist', label: 'Generalist Agent' },
  { key: 'researcher', label: 'Researcher Agent' },
  { key: 'challenger', label: 'Challenger Agent' },
  { key: 'evaluator',  label: 'Evaluator Agent'  },
];

/**
 * Add up model and provider counts across modes (Ask, Swarm, Workflow).
 *
 * @param {...(Object|null)} analytics - each mode's response, holding
 *   model_usage and provider_usage as { [name]: count }
 * @returns {{models: Object, providers: Object}} the totals by name
 */
export function buildUsageTotals(...analytics) {
  const total = (field) => {
    const totals = {};
    analytics.forEach((mode) => {
      Object.entries(mode?.[field] || {}).forEach(([name, count]) => {
        totals[name] = (totals[name] || 0) + count;
      });
    });
    return totals;
  };
  return { models: total('model_usage'), providers: total('provider_usage') };
}

/**
 * Merge sub-agent stats from two sources (Ask + Workflow), summing
 * token counts and keeping the latest model/provider seen.
 *
 * @param {Object} askMap  - { [subAgentName]: {model, provider, input_tokens, output_tokens} }
 * @param {Object} wfMap   - same shape from workflow agents sub-dict
 * @returns {Object} merged map keyed by sub-agent name
 */
function mergeSubAgents(askMap, wfMap) {
  const merged = {};
  const addEntry = (name, stats) => {
    if (!stats || typeof stats !== 'object') return;
    if (!merged[name]) merged[name] = { input_tokens: 0, output_tokens: 0 };
    merged[name].input_tokens  += stats.input_tokens  || 0;
    merged[name].output_tokens += stats.output_tokens || 0;
    if (stats.model)    merged[name].model    = stats.model;
    if (stats.provider) merged[name].provider = stats.provider;
  };
  Object.entries(askMap || {}).forEach(([n, s]) => addEntry(n, s));
  Object.entries(wfMap  || {}).forEach(([n, s]) => addEntry(n, s));
  return merged;
}

/**
 * Build unified agent cards from Ask and Workflow analytics objects.
 *
 * @param {Object|null} askAnalytics      - response from GET /api/user/usage
 * @param {Object|null} workflowAnalytics - response from GET /api/user/workflow-usage
 * @returns {Array<{
 *   key: string,
 *   label: string,
 *   totalIn: number,
 *   totalOut: number,
 *   askIn: number,
 *   askOut: number,
 *   wfIn: number,
 *   wfOut: number,
 *   wfRuns: number,
 *   subAgents: Object,
 * }>} one entry per agent type that has any recorded usage
 */
export function buildAgentCards(askAnalytics, workflowAnalytics) {
  return AGENT_TYPE_ORDER.reduce((cards, { key, label }) => {
    const askMap = askAnalytics?.[`${key}_usage`]     || {};
    const wfType = workflowAnalytics?.agent_usage?.[key] || {};
    const wfMap  = wfType.agents || {};

    const askIn  = Object.values(askMap).reduce((s, v) => s + (v?.input_tokens  || 0), 0);
    const askOut = Object.values(askMap).reduce((s, v) => s + (v?.output_tokens || 0), 0);
    const wfIn   = wfType.input_tokens  || 0;
    const wfOut  = wfType.output_tokens || 0;
    const wfRuns = wfType.runs          || 0;

    const totalIn  = askIn  + wfIn;
    const totalOut = askOut + wfOut;

    if (totalIn === 0 && totalOut === 0) return cards;

    cards.push({
      key,
      label,
      totalIn,
      totalOut,
      askIn,
      askOut,
      wfIn,
      wfOut,
      wfRuns,
      subAgents: mergeSubAgents(askMap, wfMap),
    });
    return cards;
  }, []);
}
